// DS: C136
import React, { useCallback, useContext, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Button from '@wix/da-shared-react/pkg/Button';
import { SessionContext } from '@wix/da-react-context/pkg/SessionContext';
import { PapiCorePackage, PapiUser } from '@wix/da-papi-types';
import { Url, URLS } from '@wix/da-url';
import BasicUserName from '@wix/da-shared-react/pkg/User/BasicUserName';
import { CoreTypes } from '../../../types';
import { type PackagePickerStoreSlice } from '../../../../../redux/packagePicker/types';
import { formatSaleEndDate } from '../../../utils';
import BillingCyclePackage from '../BillingCyclePackage';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { useDispatch } from 'react-redux';
import { BiData, BuyCoreButtonClickFromPPBiEvent } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';

import s from './BillingCyclePicker.scss';

export interface Props {
  giftUser?: PapiUser;
  packages: PapiCorePackage[];
  coreType: CoreTypes;
  alreadyHasSubscription: boolean;
  hasPrepaidSubscription: boolean;
  sale: PackagePickerStoreSlice['sale'];
}

const chooseDefaultInterval = (packages: PapiCorePackage[]) => {
  if (packages.some(p => p.interval === 'year')) {
    return 'year';
  }

  return packages[0].interval;
};

const BillingCyclePicker: React.FC<Props> = ({
  giftUser,
  packages,
  coreType,
  alreadyHasSubscription,
  hasPrepaidSubscription,
  sale,
}) => {
  const { t } = useTranslation();
  const { isLoggedIn } = useContext(SessionContext);
  const [selectedCycle, setSelectedCycle] = useState(
    chooseDefaultInterval(packages)
  );
  const dispatch = useDispatch();

  const pickedPackage = packages.find(p => p.interval === selectedCycle);
  const isGift = !!giftUser;

  const gotoCheckout = useCallback(() => {
    if (!selectedCycle) {
      dispatch(
        addErrorNotification(
          'Hmm sorry, but you need to select a subscription billing period.'
        )
      );
      return;
    }
    if (!pickedPackage) {
      dispatch(
        addErrorNotification(
          'Hmm sorry, but you need to select a subscription billing period.'
        )
      );
      return;
    }

    let url = Url.checkoutLink(pickedPackage.subproductId);

    if (giftUser) {
      url = `${url}&giftfor=${giftUser.userId}`;
    }

    if (!isLoggedIn) {
      let cancelUrl: string = URLS.CORE_MEMBERSHIP;
      if (giftUser) {
        cancelUrl += `/gift?subscribe=${giftUser.username.toLowerCase()}`;
      }
      url = `${Url.loginLink()}?referer=${encodeURIComponent(
        url
      )}&cancel=${encodeURIComponent(cancelUrl)}`;
    }

    window.location.assign(url);
  }, [dispatch, giftUser, isLoggedIn, pickedPackage, selectedCycle]);

  // We only show discounts during a sale (not to confuse discount w/ bulk savings).
  // Discount is displayed for one of the packages only, always giving preference to yearly.
  const shouldPackageShowDiscount = useCallback(
    (pkg: PapiCorePackage) => {
      return Boolean(
        sale?.cpShowDiscount &&
          pkg.discount &&
          (pkg.interval === 'year' ||
            !packages.find(p => p.interval === 'year')?.discount)
      );
    },
    [sale, packages]
  );
  const anyPackageHasDiscount = packages.reduce(
    (acc, pkg) => (shouldPackageShowDiscount(pkg) ? true : acc),
    false
  );

  return (
    <div className={s['root']}>
      <div className={s['content']}>
        {!isGift && (
          <h2 className={s['heading']}>
            {t('packagePicker.billingCycle.heading')}
          </h2>
        )}
        {isGift && giftUser && (
          <>
            <h2 className={s['heading']}>
              <Trans
                i18nKey={`packagePicker.billingCycle.heading.gift.${coreType}`}
              >
                Giving a Gift of CORE
                <br /> to <BasicUserName user={giftUser} />
              </Trans>
            </h2>
            <p className={s['gift-sub-heading']}>
              {t('packagePicker.billingCycle.subHeading.gift')}
            </p>
          </>
        )}
        {packages.map((pkg, i) => (
          <BillingCyclePackage
            key={`cycle-${i}`}
            pkg={pkg}
            sale={sale}
            hasDiscount={shouldPackageShowDiscount(pkg)}
            isGift={isGift}
            isSelected={selectedCycle === pkg.interval}
            onSelect={interval => setSelectedCycle(interval)}
          />
        ))}

        {alreadyHasSubscription && hasPrepaidSubscription && (
          <div>
            <span className={s['prorated-prefix']}>
              {t('packagePicker.billingCycle.proratedInfo.headerPrefix')}
            </span>
            <span className={s['prorated-header']}>
              {t('packagePicker.billingCycle.proratedInfo.header')}
            </span>
            <div className={s['prorated-info']}>
              {t('packagePicker.billingCycle.proratedInfo.text')}
            </div>
          </div>
        )}
      </div>
      <div className={s['footer']}>
        <Button
          fullWidth={false}
          className={s['footer-button']}
          view="b1-1"
          onClick={() => gotoCheckout()}
          biData={
            pickedPackage
              ? BiData<BuyCoreButtonClickFromPPBiEvent>({
                  evid: 500,
                  give_core_or_become_core: alreadyHasSubscription
                    ? 'upgrade_core'
                    : isGift
                    ? 'give_core'
                    : 'become_core',
                  membershiplength: pickedPackage.interval === 'month' ? 1 : 12,
                  membershiptypeid: pickedPackage.subproductId,
                  referral_info: ReferralType.CORE_BILLING_PAYMENTS,
                })
              : undefined
          }
        >
          {t('packagePicker.billingCycle.checkout')}
        </Button>
        <p className={s['footer-notes']}>
          {sale &&
            anyPackageHasDiscount &&
            t('packagePicker.billingCycle.saleDisclaimer', {
              endDate: formatSaleEndDate(sale.saleEndDate!),
            })}
        </p>
      </div>
    </div>
  );
};
BillingCyclePicker.displayName = 'BillingCyclePicker';

export default BillingCyclePicker;
